// MicroVariantEngine.js — pure functions, no React

const BASE_WEIGHTS = {
  standard: 70,
  ripple: 12,
  pop: 10,
  gold_burst: 0,
  cascade: 8,
};

const STREAK_OVERRIDES = [
  { threshold: 10, variants: ['cascade', 'gold_burst'] },
  { threshold: 7, variants: ['pop', 'cascade'] },
  { threshold: 4, variants: ['ripple', 'pop', 'cascade'] },
];

const RARE_CHANCE = 0.05;
const RARE_COOLDOWN = 8;

let lastRareEventIndex = -Infinity;
let eventCounter = 0;

/**
 * Select a visual variant for a micro XP event.
 * @param {Object} options
 * @param {number} options.consecutiveCorrect — current in-session streak of correct answers
 * @param {boolean} options.isPerfect — whether this was a perfect stroke (no mistakes)
 * @returns {{ variant: string, intensity: number }}
 */
export function selectVariant({ consecutiveCorrect = 0, isPerfect = false }) {
  eventCounter++;

  // Perfect accuracy always gets gold_burst
  if (isPerfect) {
    return { variant: 'gold_burst', intensity: 1.0 };
  }

  // Streak escalation — find the highest threshold met
  for (const override of STREAK_OVERRIDES) {
    if (consecutiveCorrect >= override.threshold) {
      const pick = override.variants[Math.floor(Math.random() * override.variants.length)];
      const intensity = Math.min(1.0, 0.6 + (consecutiveCorrect - override.threshold) * 0.05);
      return { variant: pick, intensity };
    }
  }

  // Rare moment check
  if (eventCounter - lastRareEventIndex >= RARE_COOLDOWN && Math.random() < RARE_CHANCE) {
    lastRareEventIndex = eventCounter;
    return { variant: 'gold_burst', intensity: 0.8 };
  }

  // Weighted random from base weights
  const totalWeight = Object.values(BASE_WEIGHTS).reduce((a, b) => a + b, 0);
  let roll = Math.random() * totalWeight;
  for (const [variant, weight] of Object.entries(BASE_WEIGHTS)) {
    roll -= weight;
    if (roll <= 0) {
      return { variant, intensity: 0.5 };
    }
  }

  return { variant: 'standard', intensity: 0.5 };
}

/**
 * Reset the engine state (call at session start).
 */
export function resetVariantEngine() {
  lastRareEventIndex = -Infinity;
  eventCounter = 0;
}
